<template>
  <Form as="div" ref="form">
    <div class="columns small-12 medium-12 large-6">
      <h1 v-html="viewModel.title" />
      <div class="push-up">
        <h3-and-tooltip
          :h3="viewModel.pensionQuestionLabel"
          :tooltip="viewModel.pensionQuestionInfoText"
        />
        <div class="row">
          <base-radio-button
            id="partnership-referral-pension-yes"
            v-model="hasExistingPension"
            :option="{ label: 'Ja', value: true }"
            :radio-class="'small-6 medium-3 large-3 radio-stacked columns'"
          />
          <base-radio-button
            id="partnership-referral-pension-no"
            v-model="hasExistingPension"
            :option="{ label: 'Nej', value: false }"
            :radio-class="'small-6 medium-3 large-3 radio-stacked columns'"
          />
        </div>
        <template v-if="hasExistingPension === 'true'">
          <info-box
            name="partnership-referral-yes-info"
            :info-box-class="'small-12 medium-8 large-8'"
            arrow-align="left"
            :text="viewModel.pensionQuestionOnYesInfoText"
          />
          <button
            data-test-partnership-referral-flow-my-page-button
            class="button large small-12 medium-6 large-6"
            @click="onReceiptButtonClick"
            v-html="receiptButton.text"
          />
        </template>
      </div>
      <div
        v-if="hasExistingPension === 'false' && hasExistingPension !== undefined"
        class="push-up small-12 medium-6 large-6"
        data-test-partnership-referral-contact-info
      >
        <h3 v-html="viewModel.contactInfoLabel" />
        <div>
          <fieldset>
            <Field
              id="firstNametextInputName"
              v-model="firstNameInput"
              type="text"
              name="partnership-referral-flow-name-input"
              v-slot="{ field }"
              :rules="'required'"
              :label="viewModel.nameLabel"
            >
              <label for="firstNameTextInputName" v-html="viewModel.nameLabel" />
              <base-text-input v-bind="field" :data-vv-as="viewModel.nameLabel" />
              <ValidationError name="partnership-referral-flow-name-input" />
            </Field>
          </fieldset>
        </div>
        <div>
          <fieldset>
            <Field
              id="telephoneTextInputName"
              v-model="phoneNumberInput"
              type="text"
              name="partnership-referral-flow-telephone-input"
              v-slot="{ field }"
              :rules="'required|numeric|digits:8'"
              :label="viewModel.phoneNumberLabel"
            >
              <label for="telephoneTextInputName" v-html="viewModel.phoneNumberLabel" />
              <base-text-input v-bind="field" :data-vv-as="viewModel.phoneNumberLabel" />
              <ValidationError name="partnership-referral-flow-telephone-input" />
            </Field>
          </fieldset>
        </div>

        <base-single-checkbox
          v-model="consentChecked"
          rules="'required:true'"
          name="partnership-referral-consent"
          :data-vv-as="viewModel.consentLabel"
          :label="viewModel.consentLabel"
        />
        <ValidationError
          name="partnership-referral-consent"
          :overrides="{ required: viewModel.consentValidationError }"
        />

        <button
          data-test-partnership-referral-accept-button
          class="button large small-12 medium-6 large-6"
          :disabled="orderCallInProgress"
          @click="orderCall"
          v-html="viewModel.acceptButtonLabel"
        />
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import { Field } from 'vee-validate';
import { defineComponent, PropType } from 'vue';
import BaseRadioButton from '@/components/Forms/BaseRadioButton.vue';
import H3AndTooltip from '@/components/UI/H3AndTooltip.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import BaseSingleCheckbox from '@/components/Forms/BaseSingleCheckbox.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import {
  PartnershipReferralFlowComponentData,
  PartnershipReferralFlowViewModel,
  PartnershipReferralOrder,
  ReceiptButton
} from '../types/PartnershipReferral';
import RequestService from '@/common/requestService';
import Utility from '@/common/UtilityFunctions.js';

export default defineComponent({
  name: 'PartnershipReferralFlow',
  components: {
    BaseRadioButton,
    H3AndTooltip,
    InfoBox,
    Field,
    BaseTextInput,
    BaseSingleCheckbox,
    ValidationError
  },
  props: {
    viewModel: {
      type: Object as PropType<PartnershipReferralFlowViewModel>,
      default() {
        return {
          title: '',
          pensionQuestionLabel: '',
          contactInfoLabel: '',
          nameLabel: '',
          nameValidationError: '',
          phoneNumberLabel: '',
          consentLabel: '',
          consentValidationError: '',
          acceptButtonLabel: '',
          pensionQuestionOnYesInfoText: '',
          pensionQuestionInfoText: ''
        };
      }
    },
    receiptButton: {
      type: Object as PropType<ReceiptButton>,
      default() {
        return {
          url: '',
          text: '',
          cssClass: ''
        };
      }
    },
    datasource: {
      type: String,
      default() {
        return '';
      }
    }
  },
  data(): PartnershipReferralFlowComponentData {
    return {
      hasExistingPension: undefined,
      firstNameInput: '',
      phoneNumberInput: '',
      consentChecked: false,
      orderCallInProgress: false
    };
  },
  methods: {
    orderCall() {
      let vm: any = this;
      vm.$refs.form.validate().then((success: boolean) => {
        if (!success) return;
        const orderModel: PartnershipReferralOrder = {
          datasource: this.datasource,
          name: this.firstNameInput,
          phoneNumber: this.phoneNumberInput,
          hasAcceptedConsent: Boolean(this.consentChecked)
        };

        this.orderCallInProgress = true;
        RequestService.post<void>(Utility.constants().API.partnership_referral_submit, orderModel)
          .then(() => {
            this.orderCallInProgress = false;
            this.$emit('order-call');
          })
          .catch((err) => {
            this.orderCallInProgress = false;
            Utility.logError(err, this);
          });
      });
    },
    onReceiptButtonClick() {
      location.href = this.receiptButton.url;
    }
  }
});
</script>
